import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

/** Super Admin–tunable polls configuration. Persisted in localStorage. */
export interface PollsConfig {
  frequencySec: number;          // min seconds between polls
  maxPollsPerMatch: number;
  pauseDuringDangerousPlays: boolean;
  mobilePosition: "below_radar" | "bottom_sheet";
  desktopPosition: "side_panel" | "bottom_right";
  sponsorPriority: boolean;      // surface sponsored polls first
}

const KEY = "vs:polls_config";

export const DEFAULT_POLLS_CONFIG: PollsConfig = {
  frequencySec: 22,
  maxPollsPerMatch: 20,
  pauseDuringDangerousPlays: true,
  mobilePosition: "below_radar",
  desktopPosition: "side_panel",
  sponsorPriority: true,
};

export function readPollsConfig(): PollsConfig {
  if (typeof window === "undefined") return DEFAULT_POLLS_CONFIG;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT_POLLS_CONFIG;
    return { ...DEFAULT_POLLS_CONFIG, ...(JSON.parse(raw) as Partial<PollsConfig>) };
  } catch {
    return DEFAULT_POLLS_CONFIG;
  }
}

export function writePollsConfig(next: Partial<PollsConfig>): PollsConfig {
  const merged = { ...readPollsConfig(), ...next };
  window.localStorage.setItem(KEY, JSON.stringify(merged));
  void trackEvent("polls_config_updated", merged as unknown as Record<string, unknown>);
  window.dispatchEvent(new CustomEvent("vs:polls_config_changed", { detail: merged }));
  return merged;
}

export function usePollsConfig(): PollsConfig {
  const [cfg, setCfg] = useState<PollsConfig>(() => readPollsConfig());
  useEffect(() => {
    const h = (e: Event) => setCfg((e as CustomEvent<PollsConfig>).detail);
    window.addEventListener("vs:polls_config_changed", h);
    return () => window.removeEventListener("vs:polls_config_changed", h);
  }, []);
  return cfg;
}